<template>
  <div class="staff-messages">
    <!-- Phone Number Selector -->
    <div class="mb-3" v-if="phoneOptions.length > 1">
      <label class="form-label"><strong>Select Phone Number</strong></label>
      <select class="form-select form-select-sm" v-model="selectedPhone">
        <option v-for="opt in phoneOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </div>
    <div class="mb-3" v-else-if="phoneOptions.length === 1">
      <small class="text-muted">
        <Phone :size="14" class="me-1" />
        Messaging: <strong>{{ phoneOptions[0].label }}</strong>
      </small>
    </div>
    <div v-else class="alert alert-warning py-2">
      <i class="fas fa-exclamation-triangle me-2"></i>
      No phone numbers on file. Add a phone number to enable messaging.
    </div>

    <!-- SMS Not Enabled Warning -->
    <div v-if="!smsEnabled && phoneOptions.length > 0" class="alert alert-info py-2">
      <i class="fas fa-info-circle me-2"></i>
      SMS messaging is not configured. Set up Elevate credentials in 
      <strong>Company Settings &gt; SMS</strong>.
    </div>

    <!-- Messages Thread -->
    <div v-if="selectedPhone && smsEnabled" class="messages-container">
      <!-- Loading state -->
      <div v-if="loading" class="text-center py-4">
        <i class="fas fa-spinner fa-spin me-2"></i> Loading messages...
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="alert alert-danger py-2">
        <i class="fas fa-exclamation-circle me-2"></i>
        {{ error }}
      </div>

      <!-- Messages list -->
      <div v-else>
        <div class="messages-thread" ref="threadContainer">
          <div v-if="messages.length === 0" class="text-center text-muted py-4">
            <MessageSquare :size="32" class="mb-2 d-block mx-auto opacity-50" />
            <small>No messages yet. Send one below.</small>
          </div>
          <div 
            v-for="msg in messages" 
            :key="msg.id"
            class="message-bubble"
            :class="{ 'message-outbound': msg.direction === 'outbound', 'message-inbound': msg.direction === 'inbound' }"
          >
            <div class="message-text">{{ msg.text }}</div>
            <div class="message-meta">
              <small>{{ formatTime(msg.timestamp) }}</small>
            </div>
          </div>
        </div>

        <!-- Compose -->
        <div class="compose-area mt-2">
          <div class="input-group">
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="newMessage"
              placeholder="Type a message..."
              @keyup.enter="sendMessage"
              :disabled="sending"
            />
            <button 
              class="btn btn-sm btn-primary" 
              @click="sendMessage"
              :disabled="sending || !newMessage.trim()"
            >
              <Send :size="14" v-if="!sending" />
              <i class="fas fa-spinner fa-spin" v-else></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { Phone, MessageSquare, Send } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import OptionsService from '@/services/OptionsService';
import Alert from '@/services/Alert';

interface PhoneOption {
  label: string;
  value: string;
}

interface SmsMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  text: string;
  timestamp: string;
}

const props = defineProps<{
  primaryPhone?: string | null;
  additionalPhones?: Array<{ number: string; type: string }> | null;
}>();

const loading = ref(false);
const sending = ref(false);
const error = ref('');
const messages = ref<SmsMessage[]>([]);
const newMessage = ref('');
const selectedPhone = ref('');
const smsEnabled = ref(false);
const threadContainer = ref<HTMLElement | null>(null);

// Build phone options from primary + additional
const phoneOptions = computed<PhoneOption[]>(() => {
  const options: PhoneOption[] = [];

  if (props.primaryPhone?.trim()) {
    options.push({
      label: `${props.primaryPhone} (Primary)`,
      value: props.primaryPhone.trim()
    });
  }

  if (props.additionalPhones) {
    for (const phone of props.additionalPhones) {
      if (phone.number?.trim()) {
        const typeLabel = phone.type === 'mobile' ? 'Mobile' : 'Landline';
        options.push({
          label: `${phone.number} (${typeLabel})`,
          value: phone.number.trim()
        });
      }
    }
  }

  return options;
});

// Auto-select first phone
watch(phoneOptions, (opts) => {
  if (opts.length > 0 && !selectedPhone.value) {
    selectedPhone.value = opts[0].value;
  }
}, { immediate: true });

// Load messages when phone changes
watch(selectedPhone, async (phone) => {
  if (phone && smsEnabled.value) {
    await loadConversations(phone);
  }
});

const checkSmsEnabled = async () => {
  try {
    const settings = await OptionsService.getElevateSettings();
    smsEnabled.value = settings.enabled && !!settings.clientId;
  } catch {
    smsEnabled.value = false;
  }
};

const loadConversations = async (phone: string) => {
  loading.value = true;
  error.value = '';
  messages.value = [];

  try {
    const data = await ApiService.getElevateConversations(phone, true);

    // Normalize the response — Intermedia's API format may vary.
    // We expect an array of messages or a conversations object with messages.
    if (Array.isArray(data)) {
      messages.value = data.map(normalizeMessage);
    } else if (data?.messages && Array.isArray(data.messages)) {
      messages.value = data.messages.map(normalizeMessage);
    } else if (data?.Messages && Array.isArray(data.Messages)) {
      messages.value = data.Messages.map(normalizeMessage);
    } else {
      messages.value = [];
    }

    await nextTick();
    scrollToBottom();
  } catch (err: any) {
    error.value = err?.message || 'Failed to load messages';
  } finally {
    loading.value = false;
  }
};

const normalizeMessage = (msg: any): SmsMessage => {
  return {
    id: msg.id || msg.Id || msg.messageId || crypto.randomUUID(),
    direction: (msg.direction || msg.Direction || 'outbound').toLowerCase(),
    text: msg.text || msg.Text || msg.body || msg.Body || '',
    timestamp: msg.timestamp || msg.Timestamp || msg.createdAt || msg.CreatedAt || new Date().toISOString()
  };
};

const sendMessage = async () => {
  if (!newMessage.value.trim() || !selectedPhone.value) return;

  sending.value = true;
  try {
    await ApiService.sendElevateSms(selectedPhone.value, newMessage.value.trim(), true);

    // Add the sent message to the local list immediately
    messages.value.push({
      id: crypto.randomUUID(),
      direction: 'outbound',
      text: newMessage.value.trim(),
      timestamp: new Date().toISOString()
    });

    newMessage.value = '';
    await nextTick();
    scrollToBottom();
  } catch (err: any) {
    Alert.notify({ text: err?.message || 'Failed to send SMS', type: 'error' });
  } finally {
    sending.value = false;
  }
};

const scrollToBottom = () => {
  if (threadContainer.value) {
    threadContainer.value.scrollTop = threadContainer.value.scrollHeight;
  }
};

const formatTime = (timestamp: string) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return timestamp;
  }
};

onMounted(() => {
  checkSmsEnabled();
});
</script>

<style scoped>
.staff-messages {
  min-height: 200px;
}

.messages-thread {
  max-height: 350px;
  overflow-y: auto;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.message-bubble {
  max-width: 80%;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  word-wrap: break-word;
}

.message-outbound {
  background: #132341;
  color: #fff;
  margin-left: auto;
  border-bottom-right-radius: 4px;
}

.message-inbound {
  background: #fff;
  color: #212529;
  border: 1px solid #dee2e6;
  margin-right: auto;
  border-bottom-left-radius: 4px;
}

.message-meta {
  opacity: 0.7;
  text-align: right;
  margin-top: 2px;
}

.message-meta small {
  font-size: 0.7rem;
}

.message-inbound .message-meta {
  text-align: left;
}

.compose-area .input-group {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.compose-area .form-control {
  border: none;
  box-shadow: none;
}

.compose-area .btn {
  border: none;
  border-radius: 0;
}
</style>
